/**
 * PaySpaceSyncService
 * Admin-only manual sync triggers + EmployeePaySpaceLink queue management.
 * Route: api/PaySpace
 */
import { BaseService } from '../base/BaseService';
export class PaySpaceSyncService extends BaseService {
    constructor(http, config) {
        const internalConfig = {
            servicePath: config?.servicePath ?? PaySpaceSyncService.DEFAULT_SERVICE_PATH,
            basePath: PaySpaceSyncService.BASE_PATH,
            endpoints: config?.endpoints,
        };
        super(http, internalConfig);
    }
    /** POST api/PaySpace/sync/employees */
    async syncEmployees() {
        const response = await this.syncEmployeesRaw();
        if (!response.data)
            throw new Error('Employee sync failed');
        return response.data;
    }
    syncEmployeesRaw() {
        return this.http.post(this.buildUrl('sync/employees'), {});
    }
    /** POST api/PaySpace/sync/leave */
    async syncLeave() {
        const response = await this.syncLeaveRaw();
        if (!response.data)
            throw new Error('Leave sync failed');
        return response.data;
    }
    syncLeaveRaw() {
        return this.http.post(this.buildUrl('sync/leave'), {});
    }
    /** GET api/PaySpace/sync/status */
    async getStatus() {
        const response = await this.getStatusRaw();
        if (!response.data)
            throw new Error('Failed to retrieve sync status');
        return response.data;
    }
    getStatusRaw() {
        return this.http.get(this.buildUrl('sync/status'));
    }
    /**
     * GET api/PaySpace/leave-balances/{ccmsUserId}?refresh=true
     *
     * Returns the employee's leave balances from PaySpace (cycle, accrued, used,
     * available). Cached for 1 hour by default; pass `refresh: true` to bypass
     * the cache and hit PaySpace directly.
     *
     * If the employee isn't linked to PaySpace, the response still resolves with
     * `isLinked: false` and an empty `balances` array — the UI can use that as
     * a "not linked" hint instead of treating it as an error.
     */
    async getLeaveBalances(ccmsUserId, refresh = false) {
        const response = await this.getLeaveBalancesRaw(ccmsUserId, refresh);
        if (!response.data)
            throw new Error(response.message || 'Failed to fetch leave balances');
        return response.data;
    }
    getLeaveBalancesRaw(ccmsUserId, refresh = false) {
        const params = {};
        if (refresh)
            params.refresh = 'true';
        return this.http.get(this.buildUrlWithParams(`leave-balances/${encodeURIComponent(ccmsUserId)}`, params));
    }
    /** GET api/PaySpace/unmatched — rows the auto-link cascade couldn't resolve */
    async getUnmatched() {
        const response = await this.getUnmatchedRaw();
        return response.data ?? [];
    }
    getUnmatchedRaw() {
        return this.http.get(this.buildUrl('unmatched'));
    }
    /** GET api/PaySpace/links?status=Linked|Unmatched|Ignored */
    async getLinks(status) {
        const response = await this.getLinksRaw(status);
        return response.data ?? [];
    }
    getLinksRaw(status) {
        const params = {};
        if (status)
            params.status = status;
        return this.http.get(this.buildUrlWithParams('links', params));
    }
    /** POST api/PaySpace/link — manual link of a PaySpace row to a CCMS user */
    async link(request) {
        const response = await this.linkRaw(request);
        if (!response.data)
            throw new Error('Link failed');
        return response.data;
    }
    linkRaw(request) {
        return this.http.post(this.buildUrl('link'), request);
    }
    /**
     * DELETE api/PaySpace/link/{ccmsUserId} — clears the link and marks the
     * row Status='Ignored' so the next sync doesn't re-link it.
     */
    async unlink(ccmsUserId) {
        const response = await this.unlinkRaw(ccmsUserId);
        return response.data ?? null;
    }
    unlinkRaw(ccmsUserId) {
        return this.http.delete(this.buildUrl(`link/${ccmsUserId}`));
    }
    // ---------- Phase 2: outbound leave outbox ----------
    /** GET api/PaySpace/outbox?status=&take= */
    async getOutbox(status, take) {
        const response = await this.getOutboxRaw(status, take);
        return response.data ?? [];
    }
    getOutboxRaw(status, take) {
        const params = {};
        if (status)
            params.status = status;
        if (take && take > 0)
            params.take = take;
        return this.http.get(this.buildUrlWithParams('outbox', params));
    }
    /** POST api/PaySpace/outbox/process — admin-triggered manual drain. */
    async processOutbox() {
        const response = await this.processOutboxRaw();
        if (!response.data)
            throw new Error('Outbox drain failed');
        return response.data;
    }
    processOutboxRaw() {
        return this.http.post(this.buildUrl('outbox/process'), {});
    }
    /** POST api/PaySpace/outbox/{outboxId}/retry */
    async retryOutbox(outboxId) {
        const response = await this.retryOutboxRaw(outboxId);
        if (!response.data)
            throw new Error(`OutboxID ${outboxId} not found`);
        return response.data;
    }
    retryOutboxRaw(outboxId) {
        return this.http.post(this.buildUrl(`outbox/${outboxId}/retry`), {});
    }
    // ---------- Phase 4: inbound webhook audit ----------
    /**
     * GET api/PaySpace/webhooks?status=&take=
     * The receive endpoint (POST api/PaySpace/webhooks) is anonymous + HMAC-verified
     * and is called by PaySpace, not the SDK.
     */
    async getWebhooks(status, take) {
        const response = await this.getWebhooksRaw(status, take);
        return response.data ?? [];
    }
    getWebhooksRaw(status, take) {
        const params = {};
        if (status)
            params.status = status;
        if (take && take > 0)
            params.take = take;
        return this.http.get(this.buildUrlWithParams('webhooks', params));
    }
    /** POST api/PaySpace/webhooks/{id}/replay — re-process a stored webhook. */
    async replayWebhook(webhookEventId) {
        const response = await this.replayWebhookRaw(webhookEventId);
        if (!response.data)
            throw new Error(`WebhookEventID ${webhookEventId} not found`);
        return response.data;
    }
    replayWebhookRaw(webhookEventId) {
        return this.http.post(this.buildUrl(`webhooks/${webhookEventId}/replay`), {});
    }
    // ---------- incremental test: one profile at a time ----------
    /** GET api/PaySpace/lookup — read-only: what PaySpace holds and who it would match. Writes nothing. */
    async lookup(employeeNumber, email) {
        const response = await this.lookupRaw(employeeNumber, email);
        if (!response.data)
            throw new Error(response.message ?? 'Lookup failed');
        return response.data;
    }
    lookupRaw(employeeNumber, email) {
        const params = {};
        if (employeeNumber)
            params.employeeNumber = employeeNumber;
        if (email)
            params.email = email;
        return this.http.get(this.buildUrlWithParams('lookup', params));
    }
    /** POST api/PaySpace/sync/employee — sync one PaySpace employee (exactly one record must match). */
    async syncEmployee(request) {
        return this.http.post(this.buildUrl('sync/employee'), request);
    }
    /** POST api/PaySpace/sync/leave/{ccmsUserId} — leave pull for one linked employee. */
    async syncLeaveForEmployee(ccmsUserId) {
        return this.http.post(this.buildUrl(`sync/leave/${encodeURIComponent(ccmsUserId)}`), {});
    }
    // ---------- HR approval of proposed links ----------
    /** POST api/PaySpace/link/{paySpaceEmployeeId}/approve */
    async approveLink(paySpaceEmployeeId) {
        return this.http.post(this.buildUrl(`link/${encodeURIComponent(paySpaceEmployeeId)}/approve`), {});
    }
    /** POST api/PaySpace/link/{paySpaceEmployeeId}/reject */
    async rejectLink(paySpaceEmployeeId, note) {
        const body = { note: note ?? null };
        return this.http.post(this.buildUrl(`link/${encodeURIComponent(paySpaceEmployeeId)}/reject`), body);
    }
    // ---------- HR approval of outbound pushes ----------
    /** POST api/PaySpace/outbox/{id}/approve — release an AwaitingApproval row to the worker. */
    async approveOutbox(outboxId) {
        return this.http.post(this.buildUrl(`outbox/${outboxId}/approve`), {});
    }
    /** POST api/PaySpace/outbox/{id}/reject */
    async rejectOutbox(outboxId, note) {
        const body = { note: note ?? null };
        return this.http.post(this.buildUrl(`outbox/${outboxId}/reject`), body);
    }
    // ---------- leave-type mapping ----------
    /** GET api/PaySpace/leave-types[?refresh=true] — PaySpace leave types with the CCMS type each maps to. */
    async getLeaveTypeMappings(refresh = false) {
        const params = {};
        if (refresh)
            params.refresh = 'true';
        const response = await this.http.get(this.buildUrlWithParams('leave-types', params));
        return response.data ?? [];
    }
    /** PUT api/PaySpace/leave-types/{code}/map — map a PaySpace code onto one CCMS leave type (null clears). */
    async mapLeaveType(paySpaceCode, ccmsLeaveTypeId) {
        const body = { ccmsLeaveTypeId };
        return this.http.put(this.buildUrl(`leave-types/${encodeURIComponent(paySpaceCode)}/map`), body);
    }
    // ---------- inbound leave review queue ----------
    /** GET api/PaySpace/leave-inbox?status=Pending|Applied|Rejected&take=N */
    async getLeaveInbox(status, take) {
        const params = {};
        if (status)
            params.status = status;
        if (take)
            params.take = String(take);
        const response = await this.http.get(this.buildUrlWithParams('leave-inbox', params));
        return response.data ?? [];
    }
    /** POST api/PaySpace/leave-inbox/{id}/apply — write the reviewed PaySpace leave into CCMS. */
    async applyLeaveInbox(inboxId) {
        return this.http.post(this.buildUrl(`leave-inbox/${inboxId}/apply`), {});
    }
    /** POST api/PaySpace/leave-inbox/{id}/reject */
    async rejectLeaveInbox(inboxId, note) {
        const body = { note: note ?? null };
        return this.http.post(this.buildUrl(`leave-inbox/${inboxId}/reject`), body);
    }
}
PaySpaceSyncService.DEFAULT_SERVICE_PATH = 'hr';
PaySpaceSyncService.BASE_PATH = 'api/PaySpace';
//# sourceMappingURL=PaySpaceSyncService.js.map